import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useQuery } from '@apollo/client';

import { ButtonModel, ModalContext } from '@notino/react-styleguide';
import { GetCustomerBenefitsQuery } from '@notino/shared/definitions/types';
import { trackEvent } from '@notino/web-tracking';

import { useFeatureFlags } from '@context/launch-darkly/LDProvider';
import { dispatchTrackingEvent } from '@context/tracking/utils';

import { MY_NOTINO_BENEFIT_PAGE } from '../../constants';
import getCustomerBenefitsQuery from '../../queries/getCustomerBenefits.graphql';
import {
  LoyaltyStatus,
  trackLoyaltyModalClose,
  trackLoyaltyModalDeprecated,
  trackLoyaltyModalOpen,
} from '../../trackLoyaltyModal';
import { BaseLoyaltyModal } from '../BaseLoyaltyModal';
import { StyledButton } from '../styled';

import { BenefitsModalTable } from './BenefitsModalTable';
import { benefitsModalTableMessages } from './BenefitsModalTable/messages';
import { benefitsModalMessages } from './messages';
import {
  BenefitsModalContentWrapper,
  ButtonWrapper,
  FooterContent,
  SettingsWrapper,
} from './styled';

export const BenefitsModal: React.FC<{ timing: number }> = ({ timing }) => {
  const { formatMessage } = useIntl();
  const { loyaltyGa4 } = useFeatureFlags();
  const { hideModal } = ModalContext.useModalContext();

  const { data } = useQuery<GetCustomerBenefitsQuery>(getCustomerBenefitsQuery);

  React.useEffect(() => {
    if (loyaltyGa4) {
      trackLoyaltyModalOpen(LoyaltyStatus.activated, timing);
    }
  }, [timing, loyaltyGa4]);

  React.useEffect(() => {
    if (!loyaltyGa4) {
      trackLoyaltyModalDeprecated(
        LoyaltyStatus.activated,
        '/overlay_vip-discount/confirmation/'
      );
    }
  }, [loyaltyGa4]);

  const handleSettingsClick = () => {
    if (loyaltyGa4) {
      dispatchTrackingEvent({
        event: 'vip_overlay_cta',
        vip: {
          action: 'click_on_overlay',
          name: 'change_settings',
        },
      });
    } else {
      trackEvent({
        eventCategory: 'Service',
        eventAction: 'LoyaltyOverlaySettings',
        eventLabel: undefined,
        eventValue: undefined,
        eventNonInteraction: undefined,
      });
    }
  };

  const handleOkClick = () => {
    if (loyaltyGa4) {
      trackLoyaltyModalClose();
    }
    hideModal();
  };

  return (
    <BaseLoyaltyModal
      hideImageOnMobile={true}
      headerMessage={formatMessage(benefitsModalMessages.header)}
      footerContent={
        <FooterContent>
          <ButtonWrapper>
            <StyledButton
              buttonSize={ButtonModel.Sizes.medium}
              buttonStyle={ButtonModel.Styles.primary}
              onClick={handleOkClick}
            >
              <FormattedMessage {...benefitsModalTableMessages.ok} />
            </StyledButton>
          </ButtonWrapper>
          <SettingsWrapper
            href={MY_NOTINO_BENEFIT_PAGE}
            onClick={handleSettingsClick}
          >
            <FormattedMessage {...benefitsModalTableMessages.settings} />
          </SettingsWrapper>
        </FooterContent>
      }
    >
      <div>
        {data?.customerBenefits && (
          <BenefitsModalContentWrapper>
            <BenefitsModalTable customerBenefits={data.customerBenefits} />
          </BenefitsModalContentWrapper>
        )}
      </div>
    </BaseLoyaltyModal>
  );
};
